import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  CalendarEventsUtil,
  CalendarPermissionsUtil,
  CalendarTimeZoneUtil,
  useApi,
  useBreakpoint,
} from '@axiom/ui';
import type {
  Account,
  User,
  OpportunityIntakeCandidateResponse,
  CalendarEventType,
  CalendarScheduledInterview,
  Taxonomy,
  AfcSubmission,
} from '@axiom/validation';
import type { EstimatedDuration } from '@axiom/const';
import {
  AccountConst,
  PermissionImpersonationRoles,
  PositionsConst,
} from '@axiom/const';

import { ImpersonationAccountPickerModal } from '../ImpersonationAccountPickerModal/ImpersonationAccountPickerModal';
import { ImpersonationClientPickerModal } from '../ImpersonationClientPickerModal/ImpersonationClientPickerModal';
import { ClientAccountPickerModal } from '../ClientAccountPickerModal/ClientAccountPickerModal';
import { TalentInterviewEngagementSelectionModal } from '../TalentInterviewEngagementSelectionModal/TalentInterviewEngagementSelectionModal';
import { TalentInterviewMiniIntakeModal } from '../TalentInterviewMiniIntakeModal/TalentInterviewMiniIntakeModal';
import { TalentInterviewWarningModal } from '../TalentInterviewWarningModal/TalentInterviewWarningModal';
import { TalentInterviewScheduleFlow } from '../TalentInterviewScheduleFlow/TalentInterviewScheduleFlow';
import { TalentInterviewPromptModal } from '../TalentInterviewPromptModal/TalentInterviewPromptModal';
import { TalentInterviewConfirmModal } from '../TalentInterviewConfirmModal/TalentInterviewConfirmModal';
import { TalentInterviewLegalTeamQuestionsModal } from '../TalentInterviewLegalTeamQuestionsModal/TalentInterviewLegalTeamQuestionsModal';
import { OpportunityIntakeApi } from '../../api/opportunity-intake-api';
import { CalendarApi } from '../../api/calendar-api';
import { SubmissionApi } from '../../api/submission-api';
import { AccountSubmissionsApi } from '../../api/account-submissions-api';
import { AccountIntakeApi } from '../../api/account-intake-api';
import { InstantMatchesApi } from '../../api/instant-matches-api';
import type { TalentInterviewMiniIntakeForm } from '../../schema/talent-interview-mini-intake-modal-schema';
import type { TalentInterviewConfirmModalType } from '../../schema/talent-interview-confirm-modal-schema';
import type { TalentInterviewLegalTeamQuestionsType } from '../../schema/talent-interview-legal-team-questions-modal-schema';
import { EngagementUtil } from '../../utils/engagement-util';
import { SubmissionsUtil } from '../../utils/submissions-util';
import type { InterviewCandidate } from '../../types/interview-candidate.types';

type TalentDirectInterviewFlowStep =
  | 'ACCOUNT_SELECTION'
  | 'CONTACT_SELECTION'
  | 'ENGAGEMENT_SELECTION'
  | 'MINI_INTAKE'
  | 'WARNING'
  | 'INTERVIEW_AUTO'
  | 'INTERVIEW_PROMPT'
  | 'LEGAL_TEAM_QUESTIONS'
  | 'INTERVIEW_CONFIRM';

export interface TalentDirectInterviewFlowProps {
  name?: string;
  candidate: InterviewCandidate;
  taxonomy: Taxonomy;
  user: User;
  onExitInterviewFlow: () => void;
}

export const TalentDirectInterviewFlow = ({
  candidate,
  taxonomy,
  user,
  onExitInterviewFlow,
}: TalentDirectInterviewFlowProps) => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const userAccounts = user.contact?.accounts ?? [];
  const isClientUser = !!user.contact;
  const hasMultipleAccounts = userAccounts.length > 1;

  // Client users: always start at engagement selection (shows all engagements from all accounts)
  // Internal users: start at account selection
  const needsImpersonationAccountSelection = !isClientUser;

  const [currentStep, setCurrentStep] = useState<TalentDirectInterviewFlowStep>(
    needsImpersonationAccountSelection
      ? 'ACCOUNT_SELECTION'
      : 'ENGAGEMENT_SELECTION'
  );
  const [account, setAccount] = useState<Account | undefined>(
    userAccounts.length === 1 ? userAccounts[0] : undefined
  );
  const [contact, setContact] = useState<Account['contacts'][number]>();
  const [intakeResponse, setIntakeResponse] =
    useState<OpportunityIntakeCandidateResponse>();
  const [submission, setSubmission] = useState<AfcSubmission>();
  const [formattedPrompt, setFormattedPrompt] = useState<string>();
  const [legalTeamData, setLegalTeamData] =
    useState<Partial<TalentInterviewLegalTeamQuestionsType>>();
  const calendarId = candidate.calendar?.id;
  const engagementContactId = contact?.id ?? user.contact?.id;

  const [{ data: calendarData } = { data: undefined }] = useApi(
    calendarId ? CalendarApi.readCalendar(calendarId) : undefined
  );

  const hasCompatibilityIssues = (
    results: OpportunityIntakeCandidateResponse['compatibilityResults']
  ): boolean => {
    return (
      !results.hasMatchingBarredLocation ||
      !results.isAvailable ||
      !results.meetsMinPayRequirements ||
      !results.jobProfileMeetsMinHoursRequirements
    );
  };

  const fetchAndSetSubmission = async (submissionId: string) => {
    SubmissionApi.clear(`/submissions/${submissionId}`);
    const submissionResult =
      await SubmissionApi.readSubmissionDetails(submissionId).toPromise();
    setSubmission(submissionResult.data);
    return submissionResult.data;
  };

  const proceedToInterviewFlow = async (
    opportunityIntakeCandidateResponse: OpportunityIntakeCandidateResponse
  ) => {
    try {
      if (!submission) {
        await fetchAndSetSubmission(
          opportunityIntakeCandidateResponse.submissionId
        );
      }

      const calendarPermissions = CalendarPermissionsUtil(
        user,
        PermissionImpersonationRoles.client
      ).userPermissions();

      const hasFreeTime = CalendarEventsUtil.hasAvailableFreeTimeSlots(
        calendarData?.events,
        calendarPermissions
      );

      if (hasFreeTime && calendarData && !isMobile) {
        setCurrentStep('INTERVIEW_AUTO');
      } else {
        setCurrentStep('INTERVIEW_PROMPT');
      }
    } catch {
      void navigate(
        `/account/${account?.id}/submission/${opportunityIntakeCandidateResponse.submissionId}/matches`
      );
    }
  };

  const handleAccountSelected = (selectedAccount: Account) => {
    setAccount(selectedAccount);

    // Client users go directly to mini intake (they already saw engagement selection)
    if (isClientUser) {
      setCurrentStep('MINI_INTAKE');
      return;
    }

    // Internal users (impersonation flow) may need contact selection
    if (selectedAccount.contacts.length > 1) {
      setCurrentStep('CONTACT_SELECTION');
    } else if (selectedAccount.contacts.length === 1) {
      setContact(selectedAccount.contacts[0]);
      setCurrentStep('ENGAGEMENT_SELECTION');
    } else {
      setCurrentStep('ENGAGEMENT_SELECTION');
    }
  };

  const handleContactSelected = (
    selectedContact: Account['contacts'][number]
  ) => {
    setContact(selectedContact);
    setCurrentStep('ENGAGEMENT_SELECTION');
  };

  const handleEngagementSelected = async (data: {
    engagementChoice: 'existing' | 'new';
    submissionId?: string;
  }) => {
    if (data.engagementChoice === 'new') {
      // Client users with multiple accounts need to select an account first
      if (isClientUser && hasMultipleAccounts && !account) {
        setCurrentStep('ACCOUNT_SELECTION');
      } else {
        setCurrentStep('MINI_INTAKE');
      }
    } else if (data.submissionId) {
      try {
        const response =
          await OpportunityIntakeApi.createOpportunityIntakeWithCandidate(
            candidate.id,
            {},
            data.submissionId
          );
        setIntakeResponse(response.data);

        // Fetch submission to get account info for client users without account set
        const fetchedSubmission = await fetchAndSetSubmission(
          response.data.submissionId
        );

        // Set account from submission for client users who bypassed account selection
        if (!account && fetchedSubmission?.accountName) {
          const matchingAccount = userAccounts.find(
            acc => acc.name === fetchedSubmission.accountName
          );
          if (matchingAccount) {
            setAccount(matchingAccount);
          }
        }

        if (hasCompatibilityIssues(response.data.compatibilityResults)) {
          setCurrentStep('WARNING');
        } else {
          await proceedToInterviewFlow(response.data);
        }
      } catch {
        onExitInterviewFlow();
      }
    }
  };

  const handleMiniIntakeSubmit = async (
    data: TalentInterviewMiniIntakeForm
  ) => {
    const primaryFocusAreaName = candidate.primaryFocusArea
      ? taxonomy.focusAreas[candidate.primaryFocusArea]?.standaloneDisplayName
      : undefined;

    const positionDescription =
      EngagementUtil.buildMiniIntakePositionDescription(
        data,
        account?.calculatedName ?? '',
        primaryFocusAreaName
      );

    const isRemote = data.worksite === PositionsConst.Worksites.Remote;
    const { addressCity, addressState, addressStreet, addressZip } = isRemote
      ? {
          addressCity: undefined,
          addressState: undefined,
          addressStreet: undefined,
          addressZip: undefined,
        }
      : EngagementUtil.extractAddressFields(data.companyLocation);
    const { addressCountryCode } = data;

    const endDate = EngagementUtil.calculateEndDateFromEstimatedDuration(
      data.startDate,
      data.estimatedDuration as EstimatedDuration
    );

    try {
      const response =
        await OpportunityIntakeApi.createOpportunityIntakeWithCandidate(
          candidate.id,
          {
            accountId: account?.id,
            contactId: contact?.id ?? user.contact?.id,
            billingUnitsPerWeek: data.billingUnitsPerWeek,
            startDate: data.startDate,
            endDate: endDate ?? undefined,
            estimatedDuration: data.estimatedDuration,
            worksite: data.worksite,
            companyLocation: isRemote ? null : data.companyLocation,
            addressCity,
            addressCountryCode,
            addressState,
            addressStreet,
            addressZip,
            primaryFocusArea: candidate.primaryFocusArea ?? undefined,
            positionDescription,
          }
        );
      setIntakeResponse(response.data);

      if (hasCompatibilityIssues(response.data.compatibilityResults)) {
        await fetchAndSetSubmission(response.data.submissionId);
        setCurrentStep('WARNING');
      } else {
        await proceedToInterviewFlow(response.data);
      }
    } catch {
      onExitInterviewFlow();
    }
  };

  const handleDataRefresh = async () => {
    return Promise.all([
      intakeResponse
        ? SubmissionApi.refreshSubmissionDetails(intakeResponse.submissionId)
        : Promise.resolve(),
      intakeResponse
        ? InstantMatchesApi.refreshInstantMatches(intakeResponse.submissionId)
        : Promise.resolve(),
      account?.id
        ? AccountIntakeApi.refreshAccountIntake(account.id)
        : Promise.resolve(),
      account?.id
        ? AccountSubmissionsApi.refreshAccountSubmissions(account.id)
        : Promise.resolve(),
      calendarId ? CalendarApi.refreshCalendar(calendarId) : Promise.resolve(),
    ]);
  };

  const navigateToMatches = () => {
    if (intakeResponse && account?.id) {
      void navigate(
        `/account/${account.id}/submission/${intakeResponse.submissionId}/matches`
      );
    } else {
      onExitInterviewFlow();
    }
  };

  const handleInterviewFlowClose = async () => {
    if (legalTeamData) {
      setLegalTeamData(undefined);
    }
    await handleDataRefresh();
    navigateToMatches();
  };

  const handleLegalTeamDataUpdated = async (
    data?: Partial<TalentInterviewLegalTeamQuestionsType>
  ) => {
    if (data && account?.id && intakeResponse) {
      await AccountIntakeApi.updateAccountIntake(account.id, data, {
        submissionId: intakeResponse.submissionId,
      });
    }
  };

  const refreshAndNavigateToMatches = async () => {
    await handleDataRefresh();
    navigateToMatches();
  };

  const handleInterviewConfirm = async (
    confirmData: TalentInterviewConfirmModalType,
    scheduledEvent?: CalendarEventType
  ) => {
    if (intakeResponse) {
      const contactId = contact?.id ?? user.contact?.id;
      const payload = {
        adhocAttendees: confirmData.adHocContacts,
        contactIds: contactId ? [contactId] : confirmData.contacts,
        preferredVideoClient: confirmData.isZoomNotPreferred
          ? null
          : AccountConst.PreferredVideoClient.zoom,
        candidateId: candidate.id,
        submissionId: intakeResponse.submissionId,
        title: scheduledEvent?.title,
        startTime: scheduledEvent?.startTime,
        endTime: scheduledEvent?.endTime,
        busy: scheduledEvent?.busy,
      } as CalendarScheduledInterview;

      await CalendarApi.createScheduledCalendarInterview(payload);
      await refreshAndNavigateToMatches();
    }
  };

  const handleManualInterviewConfirm = async (
    confirmData: TalentInterviewConfirmModalType
  ) => {
    if (intakeResponse) {
      const contactId = contact?.id ?? user.contact?.id;
      await CalendarApi.createManualInterview({
        submissionId: intakeResponse.submissionId,
        candidateId: candidate.id,
        contactIds: contactId ? [contactId] : confirmData.contacts,
        adhocAttendees: confirmData.adHocContacts,
        availabilityNote: formattedPrompt ?? confirmData.availabilityNote,
      });
      await refreshAndNavigateToMatches();
    }
  };

  const primaryContactId = SubmissionsUtil.getPrimaryOwnerId(submission);

  return (
    <>
      {currentStep === 'ACCOUNT_SELECTION' && isClientUser ? (
        <ClientAccountPickerModal
          accounts={userAccounts}
          onClose={onExitInterviewFlow}
          onContinue={handleAccountSelected}
        />
      ) : null}

      {currentStep === 'ACCOUNT_SELECTION' && !isClientUser && (
        <ImpersonationAccountPickerModal
          onClose={onExitInterviewFlow}
          selectAccount={handleAccountSelected}
        />
      )}

      {currentStep === 'CONTACT_SELECTION' && account ? (
        <ImpersonationClientPickerModal
          account={account}
          onClose={onExitInterviewFlow}
          selectContact={handleContactSelected}
          submitButtonCopy="Next"
        />
      ) : null}

      {currentStep === 'ENGAGEMENT_SELECTION' &&
      (isClientUser || account) &&
      engagementContactId ? (
        <TalentInterviewEngagementSelectionModal
          candidate={candidate}
          contactId={engagementContactId}
          onClose={onExitInterviewFlow}
          onContinue={data => {
            void handleEngagementSelected(data);
          }}
        />
      ) : null}

      {currentStep === 'MINI_INTAKE' && (
        <TalentInterviewMiniIntakeModal
          candidate={candidate}
          onExitInterviewFlow={onExitInterviewFlow}
          onContinue={async data => {
            await handleMiniIntakeSubmit(data);
          }}
          onBack={() => {
            // Client users with multiple accounts go back to account selection
            if (isClientUser && hasMultipleAccounts) {
              setAccount(undefined);
              setCurrentStep('ACCOUNT_SELECTION');
            } else {
              setCurrentStep('ENGAGEMENT_SELECTION');
            }
          }}
        />
      )}

      {currentStep === 'WARNING' && intakeResponse ? (
        <TalentInterviewWarningModal
          candidate={candidate}
          compatibilityResults={intakeResponse.compatibilityResults}
          submission={submission}
          onClose={() => {
            void (async () => {
              await handleDataRefresh();
              navigateToMatches();
            })();
          }}
          onContinue={async () => proceedToInterviewFlow(intakeResponse)}
        />
      ) : null}

      {currentStep === 'INTERVIEW_AUTO' && submission && calendarData ? (
        <TalentInterviewScheduleFlow
          accountId={account?.id}
          submission={submission}
          candidate={candidate}
          calendarData={calendarData}
          user={user}
          primaryContactId={primaryContactId}
          onClose={() => {
            void handleInterviewFlowClose();
          }}
          onConfirm={async (
            confirmData,
            scheduledEvent,
            confirmedLegalTeamData
          ) => {
            if (confirmedLegalTeamData) {
              await handleLegalTeamDataUpdated(confirmedLegalTeamData);
            }
            await handleInterviewConfirm(confirmData, scheduledEvent);
          }}
          onNoTimeAvailableFlowConfirm={async (
            confirmData,
            confirmedLegalTeamData
          ) => {
            if (confirmedLegalTeamData) {
              await handleLegalTeamDataUpdated(confirmedLegalTeamData);
            }
            await handleManualInterviewConfirm(confirmData);
          }}
        />
      ) : null}

      {currentStep === 'INTERVIEW_PROMPT' && (
        <TalentInterviewPromptModal
          candidate={candidate}
          onClose={() => {
            void handleInterviewFlowClose();
          }}
          onContinue={(timezoneId, description) => {
            const t = CalendarTimeZoneUtil.getZoneFromId(timezoneId);
            setFormattedPrompt(`${t.abbr} - ${t.name}<br>${description}`);
            if (submission?.selfService && account?.id) {
              setCurrentStep('LEGAL_TEAM_QUESTIONS');
            } else {
              setCurrentStep('INTERVIEW_CONFIRM');
            }
          }}
        />
      )}

      {currentStep === 'LEGAL_TEAM_QUESTIONS' && account?.id ? (
        <TalentInterviewLegalTeamQuestionsModal
          accountId={account.id}
          candidate={candidate}
          onClose={() => {
            void handleInterviewFlowClose();
          }}
          onContinue={data => {
            setLegalTeamData(data);
            setCurrentStep('INTERVIEW_CONFIRM');
          }}
          submissionId={submission?.submissionId}
        />
      ) : null}

      {currentStep === 'INTERVIEW_CONFIRM' && (
        <TalentInterviewConfirmModal
          candidate={candidate}
          submission={submission}
          onClose={() => {
            void handleInterviewFlowClose();
          }}
          onConfirm={async confirmData => {
            if (legalTeamData) {
              await handleLegalTeamDataUpdated(legalTeamData);
            }
            await handleManualInterviewConfirm(confirmData);
          }}
          primaryContactId={primaryContactId}
          availabilityNote={formattedPrompt}
        />
      )}
    </>
  );
};
